import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect, useSelector } from 'react-redux'
import {
    Paper, Typography, TextField, Button, Chip, Grid, Divider, Avatar,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    CircularProgress, Switch, FormControlLabel, Tooltip, IconButton,
    Dialog, DialogContent, DialogActions, Grow, Tabs, Tab, TablePagination, MenuItem,
} from '@material-ui/core'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import Autocomplete from '@material-ui/lab/Autocomplete'
import Skeleton from '@material-ui/lab/Skeleton'
import LinearProgress from '@material-ui/core/LinearProgress'
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive'
import SendIcon from '@material-ui/icons/Send'
import GroupIcon from '@material-ui/icons/Group'
import BusinessIcon from '@material-ui/icons/Business'
import LocationOnIcon from '@material-ui/icons/LocationOn'
import StoreIcon from '@material-ui/icons/Store'
import PersonIcon from '@material-ui/icons/Person'
import PublicIcon from '@material-ui/icons/Public'
import InboxIcon from '@material-ui/icons/Inbox'
import ClearIcon from '@material-ui/icons/Close'
import LockIcon from '@material-ui/icons/Lock'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import AttachFileIcon from '@material-ui/icons/AttachFile'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import MailOutlineIcon from '@material-ui/icons/MailOutline'
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile'
import LabelIcon from '@material-ui/icons/LocalOffer'
import GetAppIcon from '@material-ui/icons/GetApp'
import FilterListIcon from '@material-ui/icons/FilterList'
import AddIcon from '@material-ui/icons/Add'
import EditIcon from '@material-ui/icons/Edit'
import CheckIcon from '@material-ui/icons/Check'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import SettingsIcon from '@material-ui/icons/Settings'
import DoneAllIcon from '@material-ui/icons/DoneAll'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import moment from 'moment'
// xlsx-js-style, not xlsx: an API-compatible fork that also WRITES cell
// styles. The community build of xlsx can set a hyperlink but silently drops
// the `s` property, so the links worked yet rendered as plain black text -
// clickable, but nothing on the page said so.
import * as XLSX from 'xlsx-js-style'
import HttpService from '../../services/httpService'
import { setSnackInfo } from '../../redux/actions/appAction'
import { NOTIFICATION_SENDER_ROLE_IDS } from '../../utils/index'
import NotificationDetailSheet from '../../components/NotificationDetailSheet'

const ORANGE = '#F47E44'
const ORANGE_DARK = '#c1531f'
const ORANGE_LIGHT = '#fff1ea'
const HEADER_GRADIENT = `linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE} 60%, #ff9c66 100%)`
const INK = '#3d2b21'
const RULE = '#eceff3'

// Categories come from the database; only their TONE is a fixed key, and the
// colours for each tone live in one place so the bell, the sheet, the email
// and this page cannot drift apart.
const TONE_SWATCH = {
    blue:   { color: '#2c5fb8', soft: '#eef3fc', deep: '#2c5fb8' },
    green:  { color: '#1e7e34', soft: '#eaf6ee', deep: '#19692c' },
    amber:  { color: '#b26a00', soft: '#fff4e2', deep: '#7a4b00' },
    red:    { color: '#c0392b', soft: '#fdecea', deep: '#a93226' },
    violet: { color: '#6f40a0', soft: '#f3edf9', deep: '#5a3380' },
    teal:   { color: '#13746f', soft: '#e6f4f3', deep: '#0f5c59' },
    slate:  { color: '#4f596a', soft: '#eef1f5', deep: '#4f596a' },
    orange: { color: '#a8450f', soft: '#fff1ea', deep: '#a8450f' },
}
const swatchOf = (tone) => TONE_SWATCH[tone] || TONE_SWATCH.slate
// Order the swatches appear in the picker. Mirrors the model's enum.
const TONE_KEYS = ['blue', 'green', 'amber', 'red', 'violet', 'teal', 'slate', 'orange']

// yyyy-mm-dd, which is what <input type="date"> gives and the API expects.
// Built from the LOCAL date parts: toISOString() would shift an evening in
// IST back to the previous day.
const isoDay = (d) => [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
].join('-')

const ALL_CATEGORIES = '__all__'

// Answers the questions people actually ask of a sent list, in one click each.
const QUICK_RANGES = [
    { label: 'Today',     range: () => ({ from: isoDay(new Date()), to: isoDay(new Date()) }) },
    { label: 'Last 7 days',  range: () => ({ from: isoDay(new Date(Date.now() - 6 * 864e5)), to: isoDay(new Date()) }) },
    { label: 'Last 30 days', range: () => ({ from: isoDay(new Date(Date.now() - 29 * 864e5)), to: isoDay(new Date()) }) },
    { label: 'This month', range: () => {
        const d = new Date();
        return { from: isoDay(new Date(d.getFullYear(), d.getMonth(), 1)), to: isoDay(new Date()) }
    } },
]

// Same toolbar the task-ticket editor uses, so the two feel like one app.
// No image button on purpose: a pasted base64 image would be inlined into the
// message and sent to several hundred inboxes. Images go through Attachments.
const QUILL_MODULES = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ align: [] }],
        ['link'],
        ['clean'],
    ],
}
const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'indent', 'align', 'link']

// Quill reports an empty editor as '<p><br></p>', which is truthy, so the
// required check has to look at the text rather than the markup.
const quillToText = (html) => String(html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .trim()

const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024
const prettyBytes = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`)

const TITLE_MAX = 120
const MESSAGE_MAX = 2000
const SENT_COLUMNS = 6
const SENT_ROWS_OPTIONS = [10, 25, 50]

const BrandLinearProgress = withStyles(() => ({
    root: { height: 4, borderRadius: 999, backgroundColor: 'rgba(193,83,31,0.10)' },
    bar: { borderRadius: 999, background: `linear-gradient(90deg, ${ORANGE_DARK}, ${ORANGE}, #ff9c66)` },
}))(LinearProgress)

const useStyles = makeStyles((theme) => ({
    // Sized to the viewport so the page itself never scrolls - only the active
    // tab's own content does. 144px is the app chrome above: antd Header 64 +
    // Content margin 16*2 + Content padding 24*2 (see components/sidebar.js).
    root: {
        height: 'calc(100vh - 144px)',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        // auto, NOT hidden.
        //
        // hidden is what made zoomed-in content impossible to reach. Every
        // link below is flex:1 + minHeight:0, so children squash instead of
        // overflowing - right up until they hit their intrinsic minimum (the
        // Quill editor's 120px, the field heights). Past that the content
        // genuinely exceeded its box, and `hidden` cut it off with no
        // scrollbar anywhere to get it back.
        overflow: 'auto',
        padding: '4px 4px 8px',
        // A locked-height page fights the on-screen keyboard, and this page is
        // mostly a form, so phones keep ordinary document scrolling.
        [theme.breakpoints.down('xs')]: {
            height: 'auto', minHeight: '100vh', overflow: 'visible', padding: '4px 4px 32px',
        },
        // Browser zoom shrinks the CSS viewport: at 150% a 1080px screen
        // reports 720px, and vh shrinks with it. The WIDTH breakpoints above
        // cannot see that - a zoomed 1920px monitor is still 'md' wide - so
        // height needs a ladder of its own.
        '@media (max-height: 640px)': {
            height: 'auto', minHeight: 0, overflow: 'visible', padding: '4px 4px 32px',
        },
    },
    // The [hidden] guard is load-bearing: the hidden attribute only sets
    // display:none through the UA stylesheet, and the display here would win
    // over it, leaving both tabs rendered on top of each other.
    panelFit: {
        flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
        '&[hidden]': { display: 'none' },
        [theme.breakpoints.down('xs')]: { flex: 'none', display: 'block' },
        // A flex:1 child inside an auto-height parent collapses to nothing, so
        // this has to unlock with the root or the form vanishes entirely.
        '@media (max-height: 640px)': { flex: 'none', display: 'block' },
    },
    // The card inside the Sent tab becomes the next flex context, so the table
    // absorbs the slack and the pagination stays pinned under it.
    sentCard: {
        flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
        marginBottom: 0,
        [theme.breakpoints.down('xs')]: { flex: 'none', display: 'block' },
        '@media (max-height: 640px)': { flex: 'none', display: 'block' },
    },
    sentTableWrap: {
        flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
        // Same floor for the table - below this it is a header, one row and a
        // pager, which is not worth keeping the page locked for.
        '@media (min-height: 641px)': { minHeight: 300 },
    },

    // GroupChatWidgetIcon is position:fixed at bottom:20 right:20, 60x60, with
    // zIndex 1000 - so it sits over this corner. Rather than raising the page
    // above it (which would make the chat button unclickable here), the
    // bottom-right controls keep clear of its 100x100 footprint.
    pagination: {
        flexShrink: 0,
        borderTop: `1px solid ${RULE}`,
        paddingRight: 84,
        [theme.breakpoints.down('xs')]: { paddingRight: 0 },
    },

    // Two columns, so the whole form fits a screen instead of scrolling.
    composeGrid: {
        flex: 1, margin: 0, width: '100%',
        // The floor, and the half of the fix that makes the other half work.
        // Without it the grid shrinks forever and the root never overflows, so
        // root's `auto` would never produce a scrollbar - the content would
        // just get squashed into illegibility and then clipped. At 520 it
        // stops giving ground, overflows, and the root scrolls.
        minHeight: 520,
        [theme.breakpoints.down('sm')]: { flex: 'none', overflowY: 'auto' },
        '@media (max-height: 640px)': { flex: 'none', minHeight: 0, overflowY: 'visible' },
    },
    // Each column is its own flex context and only scrolls if its own content
    // genuinely overflows - at normal heights neither does.
    composeCol: {
        display: 'flex', flexDirection: 'column', minHeight: 0,
        paddingTop: '0 !important', paddingBottom: '0 !important',
        '& > div': { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto', marginBottom: 0 },
        // Once the page itself scrolls, a column that is also a scroller gives
        // you a scrollbar inside a scrollbar.
        '@media (max-height: 640px)': {
            display: 'block',
            '& > div': { display: 'block', overflowY: 'visible', marginBottom: 12 },
        },
    },
    banner: {
        flexShrink: 0,
        display: 'flex', alignItems: 'center', gap: 16,
        background: HEADER_GRADIENT, borderRadius: 16, padding: '16px 26px',
        marginBottom: 16, color: '#fff', boxShadow: '0 8px 24px rgba(93,55,28,0.18)',
        [theme.breakpoints.down('xs')]: { padding: '16px 18px', flexWrap: 'wrap' },
        // Between 640 and 820 the page can still fit, but only if the chrome
        // gives up the room first - about 60px, which is the difference
        // between the editor fitting and not.
        '@media (max-height: 820px)': { padding: '9px 16px', marginBottom: 8, borderRadius: 12, gap: 12 },
    },
    bannerIcon: {
        width: 46, height: 46, borderRadius: 14, flexShrink: 0,
        display: 'grid', placeItems: 'center', background: 'rgba(255,255,255,0.18)',
        '@media (max-height: 820px)': { width: 34, height: 34, borderRadius: 10 },
    },
    bannerTitle: {
        fontSize: 20, fontWeight: 700, lineHeight: 1.3,
        '@media (max-height: 820px)': { fontSize: 16 },
    },
    bannerSub: {
        fontSize: 13, color: 'rgba(255,255,255,0.85)', marginTop: 4,
        // First thing to go: it explains the page, which matters least to
        // someone already using it.
        '@media (max-height: 820px)': { display: 'none' },
    },
    restrictedChip: {
        marginLeft: 'auto', flexShrink: 0,
        background: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 700,
        [theme.breakpoints.down('xs')]: { marginLeft: 0 },
    },
    progressSlot: { height: 4, marginBottom: 10, flexShrink: 0 },
    loadingBanner: {
        flexShrink: 0,
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '9px 14px', marginBottom: 14, borderRadius: 12,
        background: ORANGE_LIGHT, border: `1px solid ${ORANGE}33`,
    },
    loadingBannerTitle: { fontSize: 13, fontWeight: 700, color: ORANGE_DARK },
    loadingBannerHint: { fontSize: 11.5, color: '#7a6a60', marginTop: 1 },
    card: {
        borderRadius: 16, border: `1px solid ${RULE}`,
        boxShadow: '0 4px 24px rgba(93,55,28,0.08)', padding: '18px 20px', marginBottom: 16,
        '@media (max-height: 820px)': { padding: '12px 14px', marginBottom: 10 },
    },
    sectionLabel: {
        display: 'flex', alignItems: 'center', gap: 8,
        fontSize: 12.5, fontWeight: 700, color: ORANGE_DARK,
        textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 14,
    },
    sectionNote: {
        fontSize: 11, fontWeight: 600, color: '#8a8079',
        textTransform: 'none', letterSpacing: 0,
    },
    counter: { fontSize: 11.5, color: '#9aa5b1', textAlign: 'right', marginTop: -6, marginBottom: 10 },
    counterOver: { color: '#c0392b', fontWeight: 700 },
    // Quill ships its own chrome; this only wraps it so it matches the
    // outlined TextFields above and below it.
    editorWrap: {
        borderRadius: 8,
        border: `1px solid rgba(0,0,0,0.23)`,
        marginBottom: 4,
        '& .ql-toolbar': {
            borderTopLeftRadius: 8, borderTopRightRadius: 8, border: 'none',
            borderBottom: `1px solid ${RULE}`, background: '#fbfaf9',
        },
        // Grows into whatever the column has spare instead of a fixed height,
        // which is what used to force the page to scroll.
        flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
        // One declaration: a second '& .ql-container' key would silently
        // replace this whole block, taking the border and font with it.
        '& .ql-container': {
            border: 'none', fontSize: 14, fontFamily: 'inherit',
            flex: 1, minHeight: 0, overflow: 'hidden',
        },
        '& .ql-editor': { minHeight: 120, height: '100%', overflowY: 'auto', lineHeight: 1.6 },
        '& .ql-editor.ql-blank::before': { fontStyle: 'normal', color: '#aab3bf' },
        '&:focus-within': { borderColor: ORANGE_DARK, boxShadow: `0 0 0 1px ${ORANGE_DARK}` },
    },
    editorError: { borderColor: '#c0392b', '&:focus-within': { borderColor: '#c0392b' } },
    fileList: { marginBottom: 10 },
    fileRow: {
        display: 'flex', alignItems: 'center', gap: 10,
        background: '#f7f9fc', border: `1px solid ${RULE}`, borderRadius: 10,
        padding: '7px 10px 7px 12px', marginBottom: 6,
    },
    fileName: {
        flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: INK,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        textDecoration: 'none', '&:hover': { textDecoration: 'underline' },
    },
    fileSize: { fontSize: 11.5, color: '#8a95a6', whiteSpace: 'nowrap' },
    emailRow: {
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 14,
        background: ORANGE_LIGHT, border: `1px solid ${ORANGE}33`, borderRadius: 12, padding: '10px 14px',
    },
    emailRowTitle: {
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontWeight: 700, fontSize: 13.5, color: INK, whiteSpace: 'nowrap',
    },
    // Tabs instead of a second route: one menu entry, and a half-written
    // message survives a look at the history.
    tabs: {
        flexShrink: 0,
        minHeight: 44, marginBottom: 14,
        borderBottom: `1px solid ${RULE}`,
        '@media (max-height: 820px)': { minHeight: 36, marginBottom: 8 },
    },
    tab: {
        minHeight: 44, minWidth: 130, textTransform: 'none',
        fontWeight: 700, fontSize: 13.5, color: '#8a95a6',
        flexDirection: 'row', gap: 8,
        '& .MuiTab-wrapper': { flexDirection: 'row', gap: 8 },
        '&.Mui-selected': { color: ORANGE_DARK },
    },
    tabIndicator: { backgroundColor: ORANGE_DARK, height: 3, borderRadius: 3 },
    newCatChip: {
        height: 26, fontSize: 11.5, fontWeight: 800,
        background: '#fff', color: ORANGE_DARK, border: `1px dashed ${ORANGE}`,
        '&:hover': { background: ORANGE_LIGHT },
    },
    catPaper: { borderRadius: 18, overflow: 'hidden' },
    catHead: {
        display: 'flex', alignItems: 'center', gap: 14, padding: '16px 22px',
        color: '#fff', background: 'linear-gradient(135deg, #9c3d12 0%, #a84315 45%, #c1531f 100%)',
    },
    catHeadIcon: {
        width: 42, height: 42, borderRadius: 13, flexShrink: 0,
        display: 'grid', placeItems: 'center', background: 'rgba(255,255,255,0.2)',
    },
    catTitle: { fontSize: 17, fontWeight: 700, lineHeight: 1.25 },
    catSub: { fontSize: 12, color: 'rgba(255,255,255,0.92)', marginTop: 2 },
    catClose: {
        color: '#fff', flexShrink: 0, marginLeft: 'auto',
        transition: 'background-color 200ms ease, transform 200ms ease',
        '&:hover': { backgroundColor: 'rgba(255,255,255,0.18)', transform: 'rotate(90deg)' },
    },
    catBody: { padding: '18px 22px 20px' },
    catFormRow: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 },
    toneLabel: {
        fontSize: 11, fontWeight: 800, letterSpacing: 0.6,
        textTransform: 'uppercase', color: '#756e68', marginBottom: 8,
    },
    toneRow: { display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 18 },
    toneDot: {
        width: 24, height: 24, borderRadius: '50%', cursor: 'pointer',
        border: '2px solid transparent', outline: 'none',
        transition: 'transform 160ms ease, box-shadow 160ms ease',
        '&:hover': { transform: 'scale(1.12)' },
        '&:focus-visible': { boxShadow: '0 0 0 3px rgba(244,126,68,0.35)' },
    },
    toneDotOn: { border: '2px solid #2f2a27', boxShadow: '0 0 0 2px #fff inset' },
    catAddBtn: {
        marginLeft: 'auto',
        borderRadius: 20, textTransform: 'none', fontWeight: 700,
        background: ORANGE_DARK, color: '#fff', padding: '5px 18px', boxShadow: 'none',
        '&:hover': { background: '#a8450f', boxShadow: 'none' },
        '&.Mui-disabled': { background: '#e6e2df', color: '#857d77' },
    },
    catListLabel: {
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9,
        fontSize: 11, fontWeight: 800, letterSpacing: 0.6,
        textTransform: 'uppercase', color: '#756e68',
    },
    sectionRuleThin: { flex: 1, height: 1, background: RULE },
    catListWrap: { position: 'relative' },
    catList: {
        maxHeight: 280, overflowY: 'auto', paddingRight: 4,
        // Room for the fade to sit over without covering the last row's border.
        paddingBottom: 2,
    },
    catFade: {
        position: 'absolute', left: 0, right: 4, bottom: 0, height: 46,
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        paddingBottom: 2, cursor: 'pointer', outline: 'none',
        background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.86) 55%, #fff 100%)',
        '&:focus-visible $catFadePill': { boxShadow: '0 0 0 3px rgba(244,126,68,0.35)' },
        '&:hover $catFadePill': { background: ORANGE_DARK, color: '#fff' },
    },
    catFadePill: {
        display: 'inline-flex', alignItems: 'center', gap: 3,
        fontSize: 10.5, fontWeight: 800, letterSpacing: 0.3,
        background: '#fff', color: ORANGE_DARK,
        border: `1px solid ${ORANGE}`, borderRadius: 999, padding: '2px 10px',
        boxShadow: '0 2px 6px rgba(93,55,28,0.14)',
        transition: 'background-color 160ms ease, color 160ms ease',
    },
    catCountPill: {
        fontSize: 10, fontWeight: 800, borderRadius: 999, padding: '1px 7px',
        background: ORANGE_LIGHT, color: '#a8450f',
    },
    scrollHintTop: {
        display: 'inline-flex', alignItems: 'center', gap: 2,
        fontSize: 10.5, fontWeight: 700, color: '#7a4b00',
        background: '#fff4e2', border: '1px solid #f3d8ab',
        borderRadius: 999, padding: '1px 8px', textTransform: 'none', letterSpacing: 0,
    },
    catRow: {
        display: 'flex', alignItems: 'center', gap: 11,
        padding: '8px 12px', borderRadius: 11, marginBottom: 6,
        border: `1px solid ${RULE}`, background: '#fff',
    },
    catRowOff: { background: '#fbfaf9', borderStyle: 'dashed' },
    catRowEdit: {
        background: ORANGE_LIGHT, borderColor: '#f3c9b2',
        boxShadow: `inset 3px 0 0 0 ${ORANGE_DARK}`, padding: '12px 12px',
    },
    editFields: { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 },
    editTones: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
    toneDotSm: {
        width: 20, height: 20, borderRadius: '50%', cursor: 'pointer',
        border: '2px solid transparent', outline: 'none',
        transition: 'transform 160ms ease',
        '&:hover': { transform: 'scale(1.14)' },
        '&:focus-visible': { boxShadow: '0 0 0 3px rgba(244,126,68,0.35)' },
    },
    editWarn: { fontSize: 11, color: '#7a4b00', fontWeight: 700, marginLeft: 6 },
    saveCatBtn: {
        marginLeft: 'auto',
        borderRadius: 18, textTransform: 'none', fontWeight: 700, fontSize: 11.5,
        background: '#19692c', color: '#fff', padding: '4px 14px', boxShadow: 'none',
        '&:hover': { background: '#11492a', boxShadow: 'none' },
        '&.Mui-disabled': { background: '#e6e2df', color: '#857d77' },
    },
    cancelCatBtn: { borderRadius: 18, textTransform: 'none', fontWeight: 600, fontSize: 11.5, color: '#6b6460' },
    catDot: { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 },
    catName: { fontSize: 13, fontWeight: 700, color: INK, display: 'flex', alignItems: 'center', gap: 7 },
    catOffTag: {
        fontSize: 9.5, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase',
        background: '#eef1f5', color: '#4f596a', borderRadius: 999, padding: '1px 7px',
    },
    catDesc: { fontSize: 11.5, color: '#5f6876', marginTop: 1 },
    catUsed: {
        fontSize: 11, fontWeight: 800, color: '#5f6876',
        background: '#eef1f5', borderRadius: 999, padding: '2px 9px', flexShrink: 0,
    },
    retireBtn: {
        borderRadius: 18, textTransform: 'none', fontWeight: 700, fontSize: 11.5,
        color: '#a93226', minWidth: 78,
    },
    restoreBtn: {
        borderRadius: 18, textTransform: 'none', fontWeight: 700, fontSize: 11.5,
        color: '#19692c', minWidth: 78,
    },
    catFootNote: {
        marginTop: 14, padding: '9px 12px', borderRadius: 11,
        background: '#eef3fc', border: '1px solid #cfdcf3',
        fontSize: 11.5, color: '#24487f', lineHeight: 1.55,
    },
    filterCard: {
        flexShrink: 0, marginBottom: 14,
        borderRadius: 14, border: `1px solid ${RULE}`,
        background: 'linear-gradient(180deg, #fcfbfa 0%, #ffffff 100%)',
        padding: '11px 14px 12px',
    },
    filterHead: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 11 },
    filterHeadText: {
        fontSize: 11, fontWeight: 800, letterSpacing: 0.7,
        textTransform: 'uppercase', color: '#756e68',
    },
    filterCount: { fontSize: 12, fontWeight: 700, color: '#5f6876', marginLeft: 6 },
    filterRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
    filterField: { background: '#fff' },
    filterDot: {
        width: 8, height: 8, borderRadius: '50%',
        display: 'inline-block', marginRight: 9, flexShrink: 0,
    },
    filterArrow: { color: '#9aa5b1', fontSize: 15, marginTop: 2 },
    quickRanges: { display: 'flex', gap: 6, flexWrap: 'wrap', marginLeft: 4 },
    quickChip: {
        height: 26, fontSize: 11.5, fontWeight: 700,
        background: '#fff', color: '#5f6876', border: `1px solid ${RULE}`,
        '&:hover': { background: ORANGE_LIGHT, color: ORANGE_DARK, borderColor: '#f3c9b2' },
    },
    quickChipOn: {
        background: ORANGE_DARK, color: '#fff', borderColor: ORANGE_DARK,
        '&:hover': { background: ORANGE_DARK, color: '#fff', borderColor: ORANGE_DARK },
    },
    activeRow: {
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        marginTop: 11, paddingTop: 10, borderTop: `1px dashed ${RULE}`,
    },
    activeLabel: {
        fontSize: 11, fontWeight: 800, letterSpacing: 0.6,
        textTransform: 'uppercase', color: '#756e68',
    },
    activeChip: {
        height: 25, fontSize: 11.5, fontWeight: 700,
        background: ORANGE_LIGHT, color: '#a8450f',
        '& .MuiChip-deleteIcon': { color: '#a8450f', fontSize: 15 },
    },
    clearFiltersBtn: {
        borderRadius: 20, textTransform: 'none', fontWeight: 600, color: ORANGE_DARK,
    },
    excelBtn: {
        marginLeft: 'auto',
        background: '#1e7e34', color: '#fff', fontWeight: 700,
        borderRadius: 10, textTransform: 'none', padding: '6px 16px',
        boxShadow: 'none',
        '&:hover': { background: '#19692c', boxShadow: 'none' },
        '&.Mui-disabled': { background: '#e6e2df', color: '#857d77' },
    },
    sevRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 4 },
    sevChip: {
        fontWeight: 700, cursor: 'pointer', border: '1px solid transparent',
        transition: 'transform 150ms ease, box-shadow 150ms ease',
        '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 3px 8px rgba(0,0,0,0.12)' },
    },
    sevHint: { fontSize: 11.5, color: '#7a6a60', marginTop: 8, display: 'block' },
    everyoneRow: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, flexWrap: 'wrap',
        background: ORANGE_LIGHT, border: `1px solid ${ORANGE}33`,
        borderRadius: 12, padding: '8px 14px', marginBottom: 14,
    },
    everyoneHint: { fontSize: 11.5, color: '#7a6a60' },
    audienceGrid: { marginBottom: 4 },
    // The number is the point of this panel, so it gets the weight.
    previewCard: {
        display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
        background: '#f7f9fc', border: `1px solid ${RULE}`, borderRadius: 12,
        padding: '14px 16px', marginTop: 10,
    },
    previewCount: { fontSize: 30, fontWeight: 700, color: INK, lineHeight: 1 },
    previewLabel: {
        fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
        color: '#8a95a6', marginTop: 4,
    },
    previewNames: { display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1, minWidth: 220 },
    nameChip: { background: '#fff', border: `1px solid ${RULE}`, fontSize: 11.5 },
    previewEmpty: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#c0392b', fontWeight: 600 },
    sendBtn: {
        background: ORANGE_DARK, color: '#fff', fontWeight: 700,
        borderRadius: 24, textTransform: 'none', padding: '8px 26px', minWidth: 170,
        boxShadow: '0 4px 14px rgba(193,83,31,0.25)',
        '&:hover': { background: '#a8441a' },
        '&.Mui-disabled': { background: '#e6e8ec', color: '#9aa5b1', boxShadow: 'none' },
    },
    ghostBtn: {
        borderRadius: 24, textTransform: 'none', fontWeight: 600,
        color: ORANGE_DARK, border: `1px solid ${ORANGE}66`, padding: '7px 20px',
        '&:hover': { background: ORANGE_LIGHT },
    },
    // marginTop:auto pins this to the bottom of the Audience card however
    // tall the pickers above it end up, so the buttons do not drift up and
    // sit mid-card when the audience is a single department.
    actions: {
        flexShrink: 0,
        display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center',
        marginTop: 'auto', paddingTop: 14, flexWrap: 'wrap',
        borderTop: `1px solid ${RULE}`,
        // GroupChatWidgetIcon is fixed at bottom-right with a 100x100
        // footprint, and this card reaches the bottom of the page.
        paddingRight: 84,
        [theme.breakpoints.down('sm')]: { paddingRight: 0, marginTop: 14 },
    },
    tableWrap: { borderRadius: 12, border: `1px solid ${RULE}`, overflow: 'hidden' },
    headCell: {
        backgroundColor: ORANGE_DARK, color: '#fff', fontWeight: 700,
        fontSize: 12.5, letterSpacing: 0.3, borderBottom: 'none', whiteSpace: 'nowrap',
    },
    bodyCell: { fontSize: 13, color: INK, verticalAlign: 'top' },
    // The whole row opens the message, so it has to look like it does - `hover`
    // alone gives a background change with no pointer.
    sentRow: {
        cursor: 'pointer',
        '&:hover $openHint': { opacity: 1 },
    },
    openHint: {
        opacity: 0, transition: 'opacity 160ms ease',
        fontSize: 11, fontWeight: 700, color: ORANGE_DARK,
        display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 4,
    },
    clamp2: {
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        overflow: 'hidden', maxWidth: 360,
    },
    emptyState: { textAlign: 'center', padding: '50px 20px', color: '#9aa5b1' },
    emptyTitle: { marginTop: 10, fontWeight: 700, color: '#7c8695' },
    emptyHint: { color: '#aab3bf', fontSize: 13, marginTop: 2 },
    deniedWrap: {
        display: 'grid', placeItems: 'center', padding: '80px 20px', textAlign: 'center', color: '#7c8695',
    },
    dlgPaper: { borderRadius: 18 },
    dlgHead: {
        display: 'flex', alignItems: 'center', gap: 14, padding: '18px 22px',
        color: '#fff', background: HEADER_GRADIENT,
    },
    dlgTitle: { fontSize: 18, fontWeight: 700 },
    dlgSub: { fontSize: 12.5, color: 'rgba(255,255,255,0.88)', marginTop: 2 },
    confirmLine: { fontSize: 14, color: INK, marginBottom: 10 },
    confirmBox: {
        display: 'flex', gap: 12, background: '#fff3e0', border: '1px solid #ffd79a',
        borderRadius: 12, padding: '12px 14px', fontSize: 12.5, color: '#7a4b00', lineHeight: 1.5,
    },
}))



const SendNotification = (props) => {
    const classes = useStyles()
    const { userDetails } = useSelector((state) => state.user)

    // Server-side gate is the real one (routes/notification.js). This only
    // keeps the page out of the way of people who cannot use it.
    const canSend = useMemo(
        () => (userDetails?.roles || []).some((r) => NOTIFICATION_SENDER_ROLE_IDS.includes(String(r?._id || r))),
        [userDetails]
    )

    const [options, setOptions] = useState({ departments: [], roles: [], cities: [], experienceCenters: [] })
    const [optionsLoading, setOptionsLoading] = useState(false)
    const [narrowedBy, setNarrowedBy] = useState({ roles: false, experienceCenters: false })

    const [title, setTitle] = useState('')
    const [messageHtml, setMessageHtml] = useState('')
    const [attachments, setAttachments] = useState([])
    const [uploading, setUploading] = useState(false)
    const [categories, setCategories] = useState([])
    const [categoriesLoading, setCategoriesLoading] = useState(true)
    const [categoryId, setCategoryId] = useState('')
    // Category management, inline - the people who can send are the only
    // people who can manage, so there is nowhere better to put it.
    const [catDialog, setCatDialog] = useState(false)
    const [catAll, setCatAll] = useState([])          // includes deactivated
    const [catAllLoading, setCatAllLoading] = useState(false)
    const [catBusyId, setCatBusyId] = useState('')
    const [newCatName, setNewCatName] = useState('')
    const [newCatDesc, setNewCatDesc] = useState('')
    const [newCatTone, setNewCatTone] = useState('slate')
    const [creatingCat, setCreatingCat] = useState(false)
    // The row being edited in place, and its draft values.
    const [editCatId, setEditCatId] = useState('')
    const [editDraft, setEditDraft] = useState({ name: '', description: '', tone: 'slate' })
    // How many rows are still below the fold. A capped, scrolling list with no
    // edge treatment reads as the end of the list - people simply did not know
    // the rest was there.
    const catListRef = useRef(null)
    const [catHiddenBelow, setCatHiddenBelow] = useState(0)

    const measureCatList = useCallback(() => {
        const el = catListRef.current
        if (!el) { setCatHiddenBelow(0); return }
        const remaining = el.scrollHeight - el.scrollTop - el.clientHeight
        if (remaining <= 4) { setCatHiddenBelow(0); return }
        // Approximate rows rather than pixels: "3 more" means something,
        // "138px more" does not.
        const rowHeight = el.scrollHeight / Math.max(catAll.length, 1)
        setCatHiddenBelow(Math.max(1, Math.round(remaining / Math.max(rowHeight, 1))))
    }, [catAll.length])

    // Re-measured when the list changes, not just on scroll: adding or
    // retiring a category changes what is below the fold.
    useEffect(() => {
        const t = setTimeout(measureCatList, 0)
        return () => clearTimeout(t)
    }, [catAll, catAllLoading, editCatId, measureCatList])
    // Sent-tab filters. ALL_CATEGORIES is a filter value meaning "do not
    // filter" - distinct from the real "All" category a notification can
    // actually be tagged with.
    const [sentCategoryId, setSentCategoryId] = useState(ALL_CATEGORIES)
    const [sentFrom, setSentFrom] = useState('')
    const [sentTo, setSentTo] = useState('')
    const [sentTotal, setSentTotal] = useState(0)
    const [exporting, setExporting] = useState(false)
    const [link, setLink] = useState('')

    const [sendToEveryone, setSendToEveryone] = useState(false)
    const [departments, setDepartments] = useState([])
    const [roles, setRoles] = useState([])
    const [cities, setCities] = useState([])
    const [experienceCenters, setExperienceCenters] = useState([])

    const [preview, setPreview] = useState({ recipientCount: 0, sample: [], summary: '' })
    const [previewLoading, setPreviewLoading] = useState(false)
    const [sending, setSending] = useState(false)
    const [confirmOpen, setConfirmOpen] = useState(false)

    const [sent, setSent] = useState([])
    const [sentLoading, setSentLoading] = useState(false)
    const [sentPage, setSentPage] = useState(0)
    const [sentRowsPerPage, setSentRowsPerPage] = useState(10)
    // The sent row being read in full. Holds the row object rather than an id,
    // so the sheet keeps rendering through a refetch of `sent` underneath it.
    const [detailNotif, setDetailNotif] = useState(null)
    // One route, two views. Two separate pages would mean two menu entries and
    // a reload that throws away a half-written message.
    const [tab, setTab] = useState(0)

    const audience = useMemo(() => ({
        sendToEveryone,
        departments: departments.map((d) => d._id),
        roles: roles.map((r) => r._id),
        cities: cities.map((c) => c._id),
        experienceCenters: experienceCenters.map((e) => e._id),
    }), [sendToEveryone, departments, roles, cities, experienceCenters])


    const hasAudience = sendToEveryone
        || departments.length > 0 || roles.length > 0 || cities.length > 0 || experienceCenters.length > 0

    // Re-fetched whenever departments or cities change, because the role list
    // and the experience-centre list DEPEND on them. Keyed on joined id
    // strings rather than the arrays themselves: the arrays are new objects on
    // every render, which would refetch forever.
    const deptKey = departments.map((d) => d._id).join(',')
    const cityKey = cities.map((c) => c._id).join(',')

    const loadOptions = useCallback(async () => {
        setOptionsLoading(true)
        try {
            const res = await HttpService.getNotificationAudienceOptions({
                departments: deptKey ? deptKey.split(',') : [],
                cities: cityKey ? cityKey.split(',') : [],
            })
            const next = {
                departments: res.data.departments || [],
                roles: res.data.roles || [],
                cities: res.data.cities || [],
                experienceCenters: res.data.experienceCenters || [],
            }
            setOptions(next)
            setNarrowedBy(res.data.narrowedBy || { roles: false, experienceCenters: false })

            // Anything already picked that the new, narrower list no longer
            // offers has to go. Leaving it selected would send to an audience
            // the picker is no longer even showing - and the chip would sit
            // there looking valid.
            setRoles((prev) => {
                const allowed = new Set(next.roles.map((r) => r._id))
                const kept = prev.filter((r) => allowed.has(r._id))
                if (kept.length !== prev.length) {
                    props.setSnackInfo(
                        `${prev.length - kept.length} role${prev.length - kept.length === 1 ? '' : 's'} removed — not held by anyone in the chosen departments`,
                        'info'
                    )
                }
                return kept
            })
            setExperienceCenters((prev) => {
                const allowed = new Set(next.experienceCenters.map((e) => e._id))
                const kept = prev.filter((e) => allowed.has(e._id))
                if (kept.length !== prev.length) {
                    props.setSnackInfo(
                        `${prev.length - kept.length} experience centre${prev.length - kept.length === 1 ? '' : 's'} removed — not in the chosen cities`,
                        'info'
                    )
                }
                return kept
            })
        } catch (err) {
            props.setSnackInfo(err?.response?.data?.message || 'Could not load the audience options', 'error')
        }
        setOptionsLoading(false)
        // eslint-disable-next-line
    }, [deptKey, cityKey])

    // Exports exactly what the filters select, not just the page on screen -
    // a file that silently differs from the table is worse than no file.
    const exportSent = async () => {
        setExporting(true)
        try {
            const res = await HttpService.getSentNotifications(sentQuery({ limit: 500, skip: 0 }))
            const rows = res.data.notifications || []
            if (!rows.length) {
                props.setSnackInfo('Nothing to export for these filters', 'warning')
                setExporting(false)
                return
            }
            // One column per attachment, sized to the busiest row, so each file
            // gets a cell of its own and therefore its own hyperlink. A single
            // comma-joined cell could only ever link to one of them.
            const maxAttachments = rows.reduce((m, n) => Math.max(m, (n.attachments || []).length), 0)

            const data = rows.map((n) => {
                const row = {
                    'Sent On': moment(n.createdAt).format('DD-MM-YYYY HH:mm'),
                    Category: n.categoryName || '',
                    Title: n.title || '',
                    Message: n.message || '',
                    Audience: n.audienceSummary || '',
                    'Sent By': n.createdByName || '',
                    Recipients: n.recipientCount || 0,
                    Opened: n.readCount || 0,
                    'Opened %': n.recipientCount ? Math.round((n.readCount / n.recipientCount) * 100) : 0,
                    Emailed: n.emailStatus?.requested ? (n.emailStatus?.sent || 0) : 'Off',
                    'Email Failed': n.emailStatus?.failed || 0,
                    Link: n.link || '',
                }
                for (let i = 0; i < maxAttachments; i += 1) {
                    const file = (n.attachments || [])[i]
                    row[`Attachment ${i + 1}`] = file ? (file.name || 'Attachment') : ''
                }
                return row
            })

            const ws = XLSX.utils.json_to_sheet(data)

            // Excel cannot follow "/cron-job-monitor" - a hyperlink needs an
            // absolute address. Non-http schemes are dropped rather than
            // written: a "javascript:" target in a spreadsheet is no better
            // than one in an href.
            const absolute = (link) => {
                if (!link) return ''
                try {
                    const url = new URL(String(link).trim(), window.location.origin)
                    return (url.protocol === 'http:' || url.protocol === 'https:') ? url.href : ''
                } catch (e) { return '' }
            }

            // Map header text -> column index, so adding or reordering a column
            // above cannot silently point the hyperlinks at the wrong cells.
            const range = XLSX.utils.decode_range(ws['!ref'])
            const colOf = {}
            for (let c = range.s.c; c <= range.e.c; c += 1) {
                const head = ws[XLSX.utils.encode_cell({ r: 0, c })]
                if (head && head.v) colOf[head.v] = c
            }

            rows.forEach((n, i) => {
                const r = i + 1   // row 0 is the header

                const linkTarget = absolute(n.link)
                if (linkTarget && colOf.Link !== undefined) {
                    const cell = ws[XLSX.utils.encode_cell({ r, c: colOf.Link })]
                    // `l` is what makes a cell a real hyperlink in SheetJS.
                    if (cell) cell.l = { Target: linkTarget, Tooltip: 'Open this page in the ERP' }
                }

                ;(n.attachments || []).forEach((file, idx) => {
                    const c = colOf[`Attachment ${idx + 1}`]
                    if (c === undefined || !file || !file.url) return
                    const cell = ws[XLSX.utils.encode_cell({ r, c })]
                    if (cell) cell.l = { Target: file.url, Tooltip: `Open ${file.name || 'attachment'}` }
                })
            })

            // Looks like a link, so people know to click it. Excel does not
            // style hyperlink cells on its own - the blue underline is a cell
            // format, not something `l` implies.
            const LINK_STYLE = {
                font: { color: { rgb: '1155CC' }, underline: true },
                alignment: { vertical: 'center' },
            };
            const HEADER_STYLE = {
                font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
                fill: { patternType: 'solid', fgColor: { rgb: 'C1531F' } },
                alignment: { vertical: 'center', horizontal: 'left' },
            };
            const BODY_STYLE = { alignment: { vertical: 'top', wrapText: true } };

            for (let c = range.s.c; c <= range.e.c; c += 1) {
                const cell = ws[XLSX.utils.encode_cell({ r: 0, c })];
                if (cell) cell.s = HEADER_STYLE;
            }
            for (let r = 1; r <= rows.length; r += 1) {
                for (let c = range.s.c; c <= range.e.c; c += 1) {
                    const cell = ws[XLSX.utils.encode_cell({ r, c })];
                    if (!cell) continue;
                    // A hyperlink keeps the link format; everything else just
                    // wraps, so a long Message does not run off the sheet.
                    cell.s = cell.l ? LINK_STYLE : BODY_STYLE;
                }
            }

            // Readable widths, otherwise Title and Message arrive as a column
            // of "####" and the file looks broken before it is even read.
            ws['!cols'] = [
                { wch: 17 }, { wch: 14 }, { wch: 38 }, { wch: 52 }, { wch: 30 },
                { wch: 18 }, { wch: 11 }, { wch: 9 }, { wch: 10 }, { wch: 10 },
                { wch: 13 }, { wch: 30 },
                ...Array.from({ length: maxAttachments }, () => ({ wch: 28 })),
            ]
            // Two things deliberately NOT set here:
            //
            // '!autofilter' - it applies no filter and hides no rows, but it
            // writes an _xlnm._FilterDatabase defined name, and Excel then
            // presents the sheet as a filtered table: arrows on every header
            // and the Filter button lit in the ribbon. The file reads as
            // though something has already been filtered out of it.
            //
            // '!freeze' - SheetJS simply does not write pane elements; the
            // string does not appear in the writer at all. Setting it looked
            // like a frozen header and produced nothing.
            ws['!rows'] = [{ hpt: 22 }]   // a little air in the header band

            const wb = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(wb, ws, 'Sent Notifications')
            const stamp = [sentFrom || 'start', sentTo || 'today'].join('_to_')
            XLSX.writeFile(wb, `Sent_Notifications_${stamp}.xlsx`)
            props.setSnackInfo(`${rows.length} notifications exported`, 'success')
        } catch (err) {
            props.setSnackInfo(err?.response?.data?.message || 'Could not export', 'error')
        }
        setExporting(false)
    }

    const loadAllCategories = async () => {
        setCatAllLoading(true)
        try {
            const res = await HttpService.getNotificationCategories({ all: true })
            setCatAll(res.data.categories || [])
        } catch (err) {
            props.setSnackInfo(err?.response?.data?.message || 'Could not load categories', 'error')
        }
        setCatAllLoading(false)
    }

    const openCatDialog = () => {
        setCatDialog(true)
        setNewCatName('')
        setNewCatDesc('')
        setNewCatTone('slate')
        loadAllCategories()
    }

    const createCategory = async () => {
        const name = newCatName.trim()
        if (!name) return
        setCreatingCat(true)
        try {
            const res = await HttpService.createNotificationCategory({
                name, description: newCatDesc.trim(), tone: newCatTone,
            })
            props.setSnackInfo(res.data.message || 'Category added', 'success')
            setNewCatName('')
            setNewCatDesc('')
            await Promise.all([loadCategories(), loadAllCategories()])
            // Selected straight away: you created it because you wanted to use
            // it on the message you are already writing.
            if (res.data.category?._id) setCategoryId(res.data.category._id)
        } catch (err) {
            // 409 carries the clash, including the "it exists but is
            // deactivated, restore it" case, which is the useful one.
            props.setSnackInfo(err?.response?.data?.message || 'Could not add that category', 'error')
        }
        setCreatingCat(false)
    }

    const startEditCat = (cat) => {
        setEditCatId(cat._id)
        setEditDraft({ name: cat.name || '', description: cat.description || '', tone: cat.tone || 'slate' })
    }

    const saveEditCat = async () => {
        const name = editDraft.name.trim()
        if (!name) return
        setCatBusyId(editCatId)
        try {
            const res = await HttpService.updateNotificationCategory(editCatId, {
                name, description: editDraft.description.trim(), tone: editDraft.tone,
            })
            // The message names how many past notifications were rewritten,
            // because editing a category does change what they say.
            props.setSnackInfo(res.data.message || 'Category updated', 'success')
            setEditCatId('')
            await Promise.all([loadCategories(), loadAllCategories()])
        } catch (err) {
            props.setSnackInfo(err?.response?.data?.message || 'Could not update that category', 'error')
        }
        setCatBusyId('')
    }

    const setCategoryActive = async (cat, isActive) => {
        setCatBusyId(cat._id)
        try {
            const res = await HttpService.setNotificationCategoryActive(cat._id, isActive)
            props.setSnackInfo(res.data.message || 'Updated', 'success')
            await Promise.all([loadCategories(), loadAllCategories()])
            // A category that has just been retired must not stay selected on
            // the message being composed - the server would reject the send.
            if (!isActive && categoryId === cat._id) setCategoryId('')
        } catch (err) {
            props.setSnackInfo(err?.response?.data?.message || 'Could not update that category', 'error')
        }
        setCatBusyId('')
    }

    const loadCategories = useCallback(async () => {
        setCategoriesLoading(true)
        try {
            const res = await HttpService.getNotificationCategories()
            setCategories(res.data.categories || [])
        } catch (err) {
            props.setSnackInfo(err?.response?.data?.message || 'Could not load categories', 'error')
        }
        setCategoriesLoading(false)
        // eslint-disable-next-line
    }, [])

    // Filters go to the SERVER, not to a slice of an already-fetched page -
    // otherwise a date range would only ever search the most recent 200.
    // Page AND filters both go to the server. Fetching 200 and slicing here
    // looked fine until a filter matched more than 200 - the pager would then
    // promise pages that had never been fetched.
    const sentQuery = useCallback((overrides = {}) => ({
        limit: sentRowsPerPage,
        skip: sentPage * sentRowsPerPage,
        categoryId: sentCategoryId === ALL_CATEGORIES ? undefined : sentCategoryId,
        from: sentFrom || undefined,
        to: sentTo || undefined,
        ...overrides,
    }), [sentCategoryId, sentFrom, sentTo, sentPage, sentRowsPerPage])

    const loadSent = useCallback(async () => {
        setSentLoading(true)
        try {
            const res = await HttpService.getSentNotifications(sentQuery())
            setSent(res.data.notifications || [])
            setSentTotal(Number(res.data.totalCount) || 0)
        } catch (err) {
            props.setSnackInfo(err?.response?.data?.message || 'Could not load what has been sent', 'error')
        }
        setSentLoading(false)
        // eslint-disable-next-line
    }, [sentQuery])

    useEffect(() => {
        if (!canSend) return
        loadCategories()
    }, [canSend, loadCategories])

    // Re-runs when departments or cities change, because loadOptions closes
    // over deptKey/cityKey.
    useEffect(() => {
        if (!canSend) return
        loadOptions()
    }, [canSend, loadOptions])

    // Re-runs whenever a filter changes, because sentQuery is in its closure.
    useEffect(() => {
        if (!canSend) return
        loadSent()
    }, [canSend, loadSent])

    // A narrowed list leaves you parked on a page that no longer exists.
    useEffect(() => { setSentPage(0) }, [sentCategoryId, sentFrom, sentTo])

    // Debounced: every tick of a multi-select would otherwise be its own count
    // query while someone is still picking.
    useEffect(() => {
        if (!canSend) return undefined
        if (!hasAudience) {
            setPreview({ recipientCount: 0, sample: [], summary: '' })
            // Not optional, and this is exactly where it used to be missed.
            //
            // resetForm() after a send empties the audience one setter at a
            // time, and src/index.js still mounts with ReactDOM.render - React
            // 18 legacy mode - so setState calls made after an await are NOT
            // batched. Each one is its own render and its own pass through this
            // effect. Clearing `departments` while `cities` is still set leaves
            // hasAudience true, so the run arms a fetch and sets loading; the
            // next setter's cleanup then cancels that fetch, and the run after
            // it lands here with an empty audience and nothing left to turn the
            // flag off. previewLoading stayed true for good: "Working out who
            // that is…" never went away, and because canSubmit includes
            // !previewLoading the Send button stayed disabled until a reload.
            setPreviewLoading(false)
            return undefined
        }
        let cancelled = false
        setPreviewLoading(true)
        const timer = setTimeout(async () => {
            try {
                const res = await HttpService.previewNotificationAudience(audience)
                if (cancelled) return
                setPreview({
                    recipientCount: res.data.recipientCount || 0,
                    sample: res.data.sample || [],
                    summary: res.data.summary || '',
                })
            } catch (err) {
                if (!cancelled) setPreview({ recipientCount: 0, sample: [], summary: '' })
            } finally {
                if (!cancelled) setPreviewLoading(false)
            }
        }, 400)
        return () => { cancelled = true; clearTimeout(timer) }
        // eslint-disable-next-line
    }, [audience, hasAudience, canSend])

    const uploadAttachment = async (e) => {
        const file = e.target.files[0]
        if (!file) return
        // Checked here as well as on the server: a 40MB file should be refused
        // before it is pushed over the wire, not after.
        if (file.size > ATTACHMENT_MAX_BYTES) {
            props.setSnackInfo(`${file.name} is ${prettyBytes(file.size)} — the limit is 10 MB`, 'warning')
            e.target.value = ''
            return
        }
        setUploading(true)
        try {
            const formData = new FormData()
            formData.append('file', file)
            const res = await HttpService.uploadNotificationAttachment(formData)
            const added = res.data.attachment
            // Keyed by url, so re-picking the same file does not list it twice.
            setAttachments((prev) => (prev.some((a) => a.url === added.url) ? prev : [...prev, added]))
        } catch (err) {
            props.setSnackInfo(err?.response?.data?.message || 'Could not upload that file', 'error')
        }
        setUploading(false)
        e.target.value = ''   // let the same file be chosen again after a mistake
    }

    const removeAttachment = (url) => {
        setAttachments((prev) => prev.filter((a) => a.url !== url))
    }

    const resetForm = () => {
        setTitle('')
        setMessageHtml('')
        setAttachments([])
        setCategoryId('')
        setLink('')
        setSendToEveryone(false)
        setDepartments([])
        setRoles([])
        setCities([])
        setExperienceCenters([])
        setPreview({ recipientCount: 0, sample: [], summary: '' })
    }

    const send = async () => {
        setConfirmOpen(false)
        setSending(true)
        try {
            const res = await HttpService.sendNotification({
                title: title.trim(),
                message: messageText,
                messageHtml,
                attachments,
                categoryId,
                link: link.trim(),
                audience,
            })
            props.setSnackInfo(res.data.message || 'Notification sent', 'success')
            resetForm()
            loadSent()
        } catch (err) {
            props.setSnackInfo(err?.response?.data?.message || 'Could not send the notification', 'error')
        }
        setSending(false)
    }

    // The editor's markup is what gets sent; this is what gets validated and
    // stored as the plain-text copy.
    const messageText = quillToText(messageHtml)

    const titleOver = title.length > TITLE_MAX
    const messageOver = messageText.length > MESSAGE_MAX
    const hasSentFilters = sentCategoryId !== ALL_CATEGORIES || Boolean(sentFrom) || Boolean(sentTo)

    const canSubmit = Boolean(title.trim()) && Boolean(messageText)
        && Boolean(categoryId)
        && hasAudience && preview.recipientCount > 0
        && !titleOver && !messageOver && !sending && !previewLoading

    if (!canSend) {
        return (
            <div className={classes.root}>
                <div className={classes.deniedWrap}>
                    <div>
                        <LockIcon style={{ fontSize: 48, color: '#c9d2de' }} />
                        <Typography className={classes.emptyTitle}>Restricted to the tech team</Typography>
                        <Typography className={classes.emptyHint}>
                            Notifications can only be sent by Admin and Naraci Tech.
                        </Typography>
                    </div>
                </div>
            </div>
        )
    }

    // `All` is a synthetic first option, not a value: picking it selects every
    // real option and is then forgotten. Storing a literal "all" would mean the
    // server had to understand a second way of saying the same thing, and the
    // audience snapshot on the notification would no longer list who it went to.
    const ALL_OPTION = { _id: '__select_all__', name: 'All' }

    const picker = (label, Icon, list, value, onChange, opts = {}) => {
        const allSelected = list.length > 0 && value.length === list.length
        const withAll = !opts.noAll && list.length > 1 && !allSelected ? [ALL_OPTION, ...list] : list
        return (
        <Grid item xs={12} sm={6}>
            <Autocomplete
                multiple
                size="small"
                disabled={sendToEveryone || Boolean(opts.disabled)}
                options={withAll}
                value={value}
                onChange={(e, v) => {
                    if (v.some((o) => o._id === ALL_OPTION._id)) { onChange(list); return }
                    onChange(v)
                }}
                getOptionLabel={(o) => o.name || ''}
                getOptionSelected={(o, v) => o._id === v._id}
                renderOption={(o) => (
                    o._id === ALL_OPTION._id ? (
                        <span style={{ fontWeight: 800, color: ORANGE_DARK, display: 'flex', alignItems: 'center', gap: 7 }}>
                            <DoneAllIcon style={{ fontSize: 16 }} />
                            Select all {list.length}
                        </span>
                    ) : (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                            <span>{o.name}</span>
                            {typeof o.userCount === 'number' && (
                                // Shows an empty role BEFORE the send rather
                                // than as a zero-recipient preview afterwards.
                                <span style={{
                                    marginLeft: 'auto', fontSize: 10.5, fontWeight: 700,
                                    color: o.userCount ? '#5f6876' : '#a93226',
                                    background: o.userCount ? '#eef1f5' : '#fdecea',
                                    borderRadius: 999, padding: '1px 7px',
                                }}>
                                    {o.userCount}
                                </span>
                            )}
                        </span>
                    )
                )}
                renderTags={(tagValue, getTagProps) => tagValue.map((option, index) => (
                    <Chip
                        {...getTagProps({ index })}
                        key={option._id}
                        size="small"
                        label={option.name}
                        style={{ background: ORANGE_LIGHT, color: ORANGE_DARK, fontWeight: 600 }}
                    />
                ))}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="outlined"
                        label={label}
                        placeholder={value.length ? '' : (opts.placeholder || 'Any')}
                        helperText={opts.helper || ' '}
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                                <>
                                    <Icon style={{ fontSize: 17, color: '#8a95a6', marginLeft: 6, marginRight: 2 }} />
                                    {params.InputProps.startAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />
        </Grid>
        )
    }

    return (
        <div className={classes.root}>

            <div className={classes.banner}>
                <div className={classes.bannerIcon}><NotificationsActiveIcon /></div>
                <div>
                    <Typography className={classes.bannerTitle}>Send Notification</Typography>
                    <Typography className={classes.bannerSub}>
                        Lands on the bell in everyone's top bar
                    </Typography>
                </div>
                <Chip
                    className={classes.restrictedChip}
                    size="small"
                    icon={<LockIcon style={{ fontSize: 15, color: '#fff' }} />}
                    label="Tech team only"
                />
            </div>

            {/* Reserved slot rather than a conditional element, so starting a
                fetch does not jog the tabs down by 4px and back again. */}
            <div className={classes.progressSlot}>
                {(optionsLoading || sending || sentLoading) ? <BrandLinearProgress /> : null}
            </div>

            {/* The bar alone says "something is happening"; this says what -
                and on a send it matters most, because the person is waiting to
                know whether several hundred people have been written to. */}
            {(optionsLoading || sending || sentLoading) && (
                <div className={classes.loadingBanner}>
                    <CircularProgress size={17} thickness={5} style={{ color: ORANGE_DARK }} />
                    <div style={{ minWidth: 0 }}>
                        <div className={classes.loadingBannerTitle}>
                            {sending
                                ? 'Sending notification…'
                                : optionsLoading
                                    ? 'Fetching departments, roles, cities and centres…'
                                    : 'Fetching sent notifications…'}
                        </div>
                        <div className={classes.loadingBannerHint}>
                            {sending
                                ? `Writing to ${preview.recipientCount || 0} ${preview.recipientCount === 1 ? 'person' : 'people'} and queueing the email — do not close this tab.`
                                : optionsLoading
                                    ? 'Loading the audience options for the pickers below.'
                                    : 'Loading what has been sent, with open counts.'}
                        </div>
                    </div>
                </div>
            )}

            <Tabs
                value={tab}
                onChange={(e, v) => setTab(v)}
                className={classes.tabs}
                TabIndicatorProps={{ className: classes.tabIndicator }}
            >
                <Tab className={classes.tab} label="Compose" icon={<SendIcon style={{ fontSize: 17 }} />} />
                <Tab
                    className={classes.tab}
                    label={sentTotal ? `Sent (${sentTotal})` : 'Sent'}
                    icon={<InboxIcon style={{ fontSize: 17 }} />}
                />
            </Tabs>

            <div className={classes.panelFit} hidden={tab !== 0}>
            <Grid container spacing={2} className={classes.composeGrid}>
            <Grid item xs={12} md={6} className={classes.composeCol}>
            {/* ------------------------------ compose ------------------------------ */}
            <Paper className={classes.card} elevation={0}>
                <div className={classes.sectionLabel}>
                    <SendIcon style={{ fontSize: 17 }} /> Message
                </div>

                <TextField
                    fullWidth variant="outlined" size="small" label="Title" required
                    placeholder="e.g. ERP down for maintenance tonight"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    error={titleOver}
                    style={{ marginBottom: 4 }}
                />
                <div className={`${classes.counter} ${titleOver ? classes.counterOver : ''}`}>
                    {title.length} / {TITLE_MAX}
                </div>

                <div className={`${classes.editorWrap} ${messageOver ? classes.editorError : ''}`}>
                    <ReactQuill
                        theme="snow"
                        value={messageHtml}
                        onChange={setMessageHtml}
                        modules={QUILL_MODULES}
                        formats={QUILL_FORMATS}
                        placeholder="What do they need to know, and what should they do about it?"
                    />
                </div>
                <div className={`${classes.counter} ${messageOver ? classes.counterOver : ''}`}>
                    {messageText.length} / {MESSAGE_MAX}
                </div>

                {/* ------------------------- attachments ------------------------- */}
                <div className={classes.sectionLabel} style={{ marginTop: 6 }}>
                    <AttachFileIcon style={{ fontSize: 16 }} /> Attachments
                </div>

                {attachments.length > 0 && (
                    <div className={classes.fileList}>
                        {attachments.map((f) => (
                            <div className={classes.fileRow} key={f.url}>
                                <InsertDriveFileIcon style={{ fontSize: 18, color: ORANGE_DARK, flexShrink: 0 }} />
                                <a className={classes.fileName} href={f.url} target="_blank" rel="noreferrer">{f.name}</a>
                                <span className={classes.fileSize}>{prettyBytes(f.size)}</span>
                                <Tooltip title="Remove">
                                    <IconButton size="small" onClick={() => removeAttachment(f.url)} disabled={sending}>
                                        <DeleteOutlineIcon style={{ fontSize: 17 }} />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        ))}
                    </div>
                )}

                <input
                    id="notification-attachment"
                    type="file"
                    style={{ display: 'none' }}
                    disabled={uploading || sending}
                    onChange={uploadAttachment}
                />
                <label htmlFor="notification-attachment">
                    <Button
                        component="span"
                        className={classes.ghostBtn}
                        disabled={uploading || sending}
                        startIcon={uploading
                            ? <CircularProgress size={14} thickness={5} style={{ color: ORANGE_DARK }} />
                            : <AttachFileIcon />}
                    >
                        {uploading ? 'Uploading…' : 'Add attachment'}
                    </Button>
                </label>
                <span className={classes.everyoneHint} style={{ marginLeft: 10 }}>
                    Up to 10 MB each. Attached to the email and linked on the bell.
                </span>

                {/* Four unlabelled coloured chips gave no clue what they were
                    for, so the group gets a heading like every other section. */}
                <div className={classes.sectionLabel} style={{ marginTop: 22 }}>
                    <LabelIcon style={{ fontSize: 16 }} /> Category <span style={{ color: '#c0392b' }}>*</span>
                </div>
                {categoriesLoading ? (
                    <Skeleton height={34} />
                ) : (
                    <div className={classes.sevRow}>
                        {categories.map((c) => {
                            const active = categoryId === c._id
                            const sw = swatchOf(c.tone)
                            return (
                                <Tooltip key={c._id} title={c.description || ''}>
                                    <Chip
                                        size="small"
                                        clickable
                                        className={classes.sevChip}
                                        label={c.name}
                                        onClick={() => setCategoryId(c._id)}
                                        style={{
                                            background: active ? sw.color : sw.soft,
                                            color: active ? '#fff' : sw.deep,
                                            borderColor: active ? sw.color : 'transparent',
                                        }}
                                    />
                                </Tooltip>
                            )
                        })}
                        {/* One control, not two: the cog and the "+ New" chip
                            opened the same dialog, which does both jobs. */}
                        <Tooltip title="Add, rename, recolour or retire categories">
                            <Chip
                                size="small"
                                clickable
                                icon={<SettingsIcon style={{ fontSize: 15, color: ORANGE_DARK }} />}
                                label="Manage"
                                className={classes.newCatChip}
                                onClick={openCatDialog}
                            />
                        </Tooltip>
                        {!categories.length && (
                            <span className={classes.sevHint} style={{ color: '#a93226' }}>
                                No categories yet — add one with <b>New</b>.
                            </span>
                        )}
                    </div>
                )}
                <span className={classes.sevHint}>
                    Says what this is about. It shows on the bell, on the email band and in the
                    Sent list, and it is what people filter by later.
                </span>

                <TextField
                    fullWidth variant="outlined" size="small"
                    label="Link (optional)"
                    placeholder="/cron-job-monitor"
                    helperText="An in-app path. The notification becomes clickable when this is set."
                    value={link}
                    onChange={(e) => setLink(e.target.value)}
                    style={{ marginTop: 18 }}
                />
            </Paper>

            </Grid>

            <Grid item xs={12} md={6} className={classes.composeCol}>
            {/* ------------------------------ audience ----------------------------- */}
            <Paper className={classes.card} elevation={0}>
                <div className={classes.sectionLabel}>
                    <GroupIcon style={{ fontSize: 17 }} /> Audience
                </div>

                <div className={classes.everyoneRow}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={sendToEveryone}
                                onChange={(e) => setSendToEveryone(e.target.checked)}
                                color="primary"
                                size="small"
                            />
                        }
                        label={<span style={{ fontWeight: 700, fontSize: 13.5, color: INK }}>Send to everyone</span>}
                    />
                    <span className={classes.everyoneHint}>
                        Every active user. The filters below are ignored while this is on.
                    </span>
                </div>

                {optionsLoading ? (
                    <Grid container spacing={2}>
                        {[0, 1, 2, 3].map((i) => (
                            <Grid item xs={12} sm={6} key={i}><Skeleton height={48} /></Grid>
                        ))}
                    </Grid>
                ) : (
                    <Grid container spacing={2} className={classes.audienceGrid}>
                        {/* Department deliberately has NO "All" row: picking
                            every department is what "Send to everyone" above
                            already means, and two ways to say the same thing
                            is how an audience ends up wrong. */}
                        {picker('Department', BusinessIcon, options.departments, departments, setDepartments, {
                            placeholder: 'Any department',
                            helper: 'Narrows the Role list below',
                            noAll: true,
                        })}
                        {picker('Role', PersonIcon, options.roles, roles, setRoles, {
                            placeholder: optionsLoading ? 'Loading…' : 'Any role',
                            helper: narrowedBy.roles
                                ? `${options.roles.length} roles held in the chosen department${departments.length === 1 ? '' : 's'}`
                                : 'All roles · pick a department to narrow',
                        })}
                        {picker('City', LocationOnIcon, options.cities, cities, setCities, {
                            placeholder: 'Any city',
                            helper: 'Narrows the Experience Centre list',
                        })}
                        {picker('Experience Centre', StoreIcon, options.experienceCenters, experienceCenters, setExperienceCenters, {
                            placeholder: optionsLoading ? 'Loading…' : 'Any centre',
                            helper: narrowedBy.experienceCenters
                                ? `${options.experienceCenters.length} centre${options.experienceCenters.length === 1 ? '' : 's'} in the chosen cit${cities.length === 1 ? 'y' : 'ies'}`
                                : 'All centres · pick a city to narrow',
                        })}
                    </Grid>
                )}

                {/* The rule is not symmetrical and getting it wrong is only
                    visible after the message has gone out, so it is stated. */}
                {!sendToEveryone && (
                    <Typography className={classes.everyoneHint} style={{ marginTop: 10, display: 'block' }}>
                        Picks within one box are combined with <b>OR</b>; the boxes are combined with <b>AND</b>.
                        Department “Sales” + City “Bangalore” reaches Sales people in Bangalore, not both groups.
                    </Typography>
                )}

                <div className={classes.previewCard}>
                    {previewLoading ? (
                        <>
                            <CircularProgress size={22} thickness={5} style={{ color: ORANGE_DARK }} />
                            <span style={{ fontSize: 13, color: '#7c8695' }}>Working out who that is…</span>
                        </>
                    ) : !hasAudience ? (
                        <span style={{ fontSize: 13, color: '#7c8695' }}>
                            Pick an audience to see who will receive this.
                        </span>
                    ) : preview.recipientCount === 0 ? (
                        <span className={classes.previewEmpty}>
                            <ErrorOutlineIcon style={{ fontSize: 18 }} />
                            No active user matches that combination — nothing would be sent.
                        </span>
                    ) : (
                        <>
                            <div>
                                <div className={classes.previewCount}>{preview.recipientCount}</div>
                                <div className={classes.previewLabel}>
                                    {preview.recipientCount === 1 ? 'Recipient' : 'Recipients'}
                                </div>
                            </div>
                            <Divider orientation="vertical" flexItem />
                            <div className={classes.previewNames}>
                                {preview.sample.map((u) => (
                                    <Chip key={u._id} size="small" className={classes.nameChip} label={u.name} />
                                ))}
                                {preview.recipientCount > preview.sample.length && (
                                    <Chip
                                        size="small"
                                        className={classes.nameChip}
                                        label={`+${preview.recipientCount - preview.sample.length} more`}
                                        style={{ fontWeight: 700 }}
                                    />
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Not a choice any more - every notification is emailed too.
                    Stated rather than hidden, because it changes how carefully
                    someone writes the message. */}
                <div className={classes.emailRow}>
                    <span className={classes.emailRowTitle}>
                        <MailOutlineIcon style={{ fontSize: 17, color: ORANGE_DARK }} />
                        Also email
                    </span>
                    <span className={classes.everyoneHint}>
                        {preview.recipientCount > 0
                            ? `Goes to the same ${preview.recipientCount} ${preview.recipientCount === 1 ? 'inbox' : 'inboxes'}, in batches, with the attachments.`
                            : 'Every recipient gets this on the bell and in their inbox.'}
                    </span>
                </div>

                {/* Inside the Audience card, pushed to its bottom edge by
                    marginTop:auto. Floating under both columns left them on
                    bare page background with nothing to sit on, and next to
                    the recipient count is where the decision is made anyway. */}
                <div className={classes.actions}>
                    <Button className={classes.ghostBtn} onClick={resetForm} disabled={sending}>
                        Clear
                    </Button>
                    <Button
                        className={classes.sendBtn}
                        disabled={!canSubmit}
                        onClick={() => setConfirmOpen(true)}
                        startIcon={sending
                            ? <CircularProgress size={15} thickness={5} style={{ color: '#fff' }} />
                            : <SendIcon />}
                    >
                        {sending ? 'Sending…' : `Send to ${preview.recipientCount || 0}`}
                    </Button>
                </div>

            </Paper>
            </Grid>
            </Grid>

            </div>

            <div className={classes.panelFit} hidden={tab !== 1}>
            {/* -------------------------------- sent ------------------------------- */}
            <Paper className={`${classes.card} ${classes.sentCard}`} elevation={0}>
                <div className={classes.filterCard}>
                    <div className={classes.filterHead}>
                        <FilterListIcon style={{ fontSize: 16, color: ORANGE_DARK }} />
                        <span className={classes.filterHeadText}>Filters</span>
                        <span className={classes.filterCount}>
                            {sentLoading ? 'Loading…' : `${sentTotal} notification${sentTotal === 1 ? '' : 's'}`}
                        </span>
                        <Button
                            size="small"
                            className={classes.excelBtn}
                            disabled={exporting || sentLoading || !sentTotal}
                            startIcon={exporting
                                ? <CircularProgress size={14} thickness={5} style={{ color: '#fff' }} />
                                : <GetAppIcon style={{ fontSize: 17 }} />}
                            onClick={exportSent}
                        >
                            {exporting ? 'Preparing…' : 'Excel'}
                        </Button>
                    </div>

                    <div className={classes.filterRow}>
                        <TextField
                            select size="small" variant="outlined" label="Category"
                            value={sentCategoryId}
                            onChange={(e) => setSentCategoryId(e.target.value)}
                            className={classes.filterField}
                            style={{ minWidth: 200 }}
                        >
                            {/* "All categories" is a FILTER value - it is never
                                stored on a notification. The real "All"
                                category appears below it with the rest. */}
                            <MenuItem value={ALL_CATEGORIES}><em>All categories</em></MenuItem>
                            {categories.map((c) => {
                                const sw = swatchOf(c.tone)
                                return (
                                    <MenuItem key={c._id} value={c._id}>
                                        <span className={classes.filterDot} style={{ background: sw.color }} />
                                        {c.name}
                                    </MenuItem>
                                )
                            })}
                        </TextField>

                        <TextField
                            type="date" size="small" variant="outlined" label="From"
                            value={sentFrom}
                            onChange={(e) => setSentFrom(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ max: sentTo || isoDay(new Date()) }}
                            className={classes.filterField}
                            style={{ width: 158 }}
                        />
                        <span className={classes.filterArrow}>&rarr;</span>
                        <TextField
                            type="date" size="small" variant="outlined" label="To"
                            value={sentTo}
                            onChange={(e) => setSentTo(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            // Bounded by From, so an impossible range cannot be
                            // typed and then silently return nothing.
                            inputProps={{ min: sentFrom || undefined, max: isoDay(new Date()) }}
                            className={classes.filterField}
                            style={{ width: 158 }}
                        />

                        {/* Typing two dates to answer "what went out this week"
                            is four interactions; this is one. */}
                        <span className={classes.quickRanges}>
                            {QUICK_RANGES.map((r) => {
                                const range = r.range()
                                const active = sentFrom === range.from && sentTo === range.to
                                return (
                                    <Chip
                                        key={r.label}
                                        size="small"
                                        clickable
                                        label={r.label}
                                        className={`${classes.quickChip} ${active ? classes.quickChipOn : ''}`}
                                        onClick={() => {
                                            if (active) { setSentFrom(''); setSentTo(''); return }
                                            setSentFrom(range.from); setSentTo(range.to)
                                        }}
                                    />
                                )
                            })}
                        </span>
                    </div>

                    {hasSentFilters && (
                        <div className={classes.activeRow}>
                            <span className={classes.activeLabel}>Showing</span>
                            {sentCategoryId !== ALL_CATEGORIES && (
                                <Chip
                                    size="small" className={classes.activeChip}
                                    label={categories.find((c) => c._id === sentCategoryId)?.name || 'Category'}
                                    onDelete={() => setSentCategoryId(ALL_CATEGORIES)}
                                />
                            )}
                            {(sentFrom || sentTo) && (
                                <Chip
                                    size="small" className={classes.activeChip}
                                    label={`${sentFrom ? moment(sentFrom).format('DD MMM YYYY') : 'the start'} → ${sentTo ? moment(sentTo).format('DD MMM YYYY') : 'today'}`}
                                    onDelete={() => { setSentFrom(''); setSentTo('') }}
                                />
                            )}
                            <Button
                                size="small"
                                className={classes.clearFiltersBtn}
                                startIcon={<ClearIcon style={{ fontSize: 14 }} />}
                                onClick={() => { setSentCategoryId(ALL_CATEGORIES); setSentFrom(''); setSentTo('') }}
                            >
                                Clear all
                            </Button>
                        </div>
                    )}
                </div>

                <div className={classes.sectionLabel}>
                    <InboxIcon style={{ fontSize: 17 }} /> Recently sent
                    {sentTotal > 0 && (
                        <Chip size="small" label={sentTotal} style={{ background: ORANGE_LIGHT, color: ORANGE_DARK, fontWeight: 700, height: 20 }} />
                    )}
                    {sentTotal > sent.length && sent.length > 0 && (
                        <span className={classes.sectionNote}>
                            showing {sentPage * sentRowsPerPage + 1}–{sentPage * sentRowsPerPage + sent.length} of {sentTotal}
                        </span>
                    )}
                </div>
                <div className={`${classes.tableWrap} ${classes.sentTableWrap}`}>
                    <TableContainer style={{ flex: 1, minHeight: 0 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell className={classes.headCell}>Title</TableCell>
                                    <TableCell className={classes.headCell}>Audience</TableCell>
                                    <TableCell className={classes.headCell}>Category</TableCell>
                                    <TableCell className={classes.headCell}>Sent by</TableCell>
                                    <TableCell className={classes.headCell}>Sent</TableCell>
                                    <TableCell className={classes.headCell} align="right">Opened</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sentLoading ? (
                                    [...Array(4)].map((_, i) => (
                                        <TableRow key={`sk-${i}`}>
                                            {[...Array(SENT_COLUMNS)].map((__, c) => (
                                                <TableCell key={c}><Skeleton height={20} /></TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : !sent.length ? (
                                    <TableRow>
                                        <TableCell colSpan={SENT_COLUMNS} style={{ borderBottom: 'none' }}>
                                            <div className={classes.emptyState}>
                                                <InboxIcon style={{ fontSize: 46, color: '#c9d2de' }} />
                                                <Typography className={classes.emptyTitle}>Nothing sent yet</Typography>
                                                <Typography className={classes.emptyHint}>
                                                    Anything you send will be listed here with how many people opened it.
                                                </Typography>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    sent.map((n) => {
                                        const sw = swatchOf(n.categoryTone)
                                        const pct = n.recipientCount ? Math.round((n.readCount / n.recipientCount) * 100) : 0
                                        return (
                                            <TableRow
                                                key={n._id}
                                                hover
                                                className={classes.sentRow}
                                                onClick={() => setDetailNotif(n)}
                                            >
                                                <TableCell className={classes.bodyCell}>
                                                    <div style={{ fontWeight: 700 }}>{n.title}</div>
                                                    <div className={classes.clamp2} style={{ fontSize: 12, color: '#8a95a6' }}>
                                                        {n.message}
                                                    </div>
                                                    <div className={classes.openHint}>
                                                        <OpenInNewIcon style={{ fontSize: 12 }} /> Click to read
                                                    </div>
                                                </TableCell>
                                                <TableCell className={classes.bodyCell}>
                                                    <Chip
                                                        size="small"
                                                        icon={n.audience?.sendToEveryone
                                                            ? <PublicIcon style={{ fontSize: 15, color: ORANGE_DARK }} />
                                                            : <GroupIcon style={{ fontSize: 15, color: ORANGE_DARK }} />}
                                                        label={n.audienceSummary || '—'}
                                                        style={{ background: ORANGE_LIGHT, color: ORANGE_DARK, fontWeight: 600, maxWidth: 260 }}
                                                    />
                                                </TableCell>
                                                <TableCell className={classes.bodyCell}>
                                                    <Chip
                                                        size="small"
                                                        label={n.categoryName || '—'}
                                                        style={{ background: sw.soft, color: sw.deep, fontWeight: 700 }}
                                                    />
                                                </TableCell>
                                                <TableCell className={classes.bodyCell} style={{ whiteSpace: 'nowrap' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <Avatar style={{ width: 26, height: 26, fontSize: 11, fontWeight: 700, background: ORANGE_LIGHT, color: ORANGE_DARK }}>
                                                            {String(n.createdByName || '?').trim().charAt(0).toUpperCase()}
                                                        </Avatar>
                                                        {n.createdByName || '—'}
                                                    </div>
                                                </TableCell>
                                                <TableCell className={classes.bodyCell} style={{ whiteSpace: 'nowrap' }}>
                                                    {moment(n.createdAt).format('DD MMM, h:mm A')}
                                                    <div style={{ fontSize: 11.5, color: '#99a3b2' }}>
                                                        {moment(n.createdAt).fromNow()}
                                                    </div>
                                                </TableCell>
                                                <TableCell className={classes.bodyCell} align="right" style={{ whiteSpace: 'nowrap' }}>
                                                    <b>{n.readCount}</b> / {n.recipientCount}
                                                    <div style={{ fontSize: 11.5, color: '#99a3b2' }}>{pct}% opened</div>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {/* Paginated client-side: /sent already returns the whole
                        window in one call, so paging here costs no requests
                        and keeps the controls pinned under the table. */}
                    {/* Shown whenever there is anything to show. The old
                        condition was `sent.length > 10`, so with nine
                        notifications the pager - and the rows-per-page
                        control with it - simply did not exist. */}
                    {!sentLoading && sentTotal > 0 && (
                        <TablePagination
                            className={classes.pagination}
                            component="div"
                            count={sentTotal}
                            page={sentPage}
                            onChangePage={(event, p) => setSentPage(p)}
                            rowsPerPage={sentRowsPerPage}
                            onChangeRowsPerPage={(event) => {
                                setSentRowsPerPage(parseInt(event.target.value, 10))
                                setSentPage(0)
                            }}
                            rowsPerPageOptions={SENT_ROWS_OPTIONS}
                        />
                    )}
                </div>
            </Paper>

            </div>

            {/* A send cannot be recalled, so the count is confirmed first. */}
            {/* Manage categories. Create and retire only - renaming would
                rewrite what past announcements say they were about, since the
                name is snapshotted onto every notification at send time. */}
            <Dialog
                open={catDialog}
                onClose={() => setCatDialog(false)}
                fullWidth
                maxWidth="sm"
                classes={{ paper: classes.catPaper }}
            >
                <div className={classes.catHead}>
                    <div className={classes.catHeadIcon}><LabelIcon /></div>
                    <div style={{ minWidth: 0 }}>
                        <div className={classes.catTitle}>Categories</div>
                        <div className={classes.catSub}>Add your own, retire the ones you no longer use</div>
                    </div>
                    <IconButton size="small" className={classes.catClose} onClick={() => setCatDialog(false)} aria-label="close">
                        <ClearIcon fontSize="small" />
                    </IconButton>
                </div>

                <DialogContent className={classes.catBody}>
                    <div className={classes.catFormRow}>
                        <TextField
                            size="small" variant="outlined" label="New category"
                            placeholder="e.g. Flow Change"
                            value={newCatName}
                            onChange={(e) => setNewCatName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && newCatName.trim() && !creatingCat) createCategory() }}
                            inputProps={{ maxLength: 60 }}
                            style={{ flex: '1 1 180px' }}
                        />
                        <TextField
                            size="small" variant="outlined" label="Description (optional)"
                            placeholder="Shown under the name in the picker"
                            value={newCatDesc}
                            onChange={(e) => setNewCatDesc(e.target.value)}
                            inputProps={{ maxLength: 160 }}
                            style={{ flex: '1 1 220px' }}
                        />
                    </div>

                    <div className={classes.toneLabel}>Colour</div>
                    <div className={classes.toneRow}>
                        {/* A fixed palette, not a colour picker: each tone is a
                            set of values already checked to stay readable on
                            the bell, the hero and the email together. */}
                        {TONE_KEYS.map((t) => {
                            const sw = swatchOf(t)
                            const active = newCatTone === t
                            return (
                                <Tooltip key={t} title={t}>
                                    <span
                                        role="button"
                                        tabIndex={0}
                                        aria-label={t}
                                        aria-pressed={active}
                                        className={`${classes.toneDot} ${active ? classes.toneDotOn : ''}`}
                                        style={{ background: sw.color }}
                                        onClick={() => setNewCatTone(t)}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setNewCatTone(t) } }}
                                    />
                                </Tooltip>
                            )
                        })}
                        <Chip
                            size="small"
                            label={newCatName.trim() || 'Preview'}
                            style={{
                                marginLeft: 8, fontWeight: 700,
                                background: swatchOf(newCatTone).soft,
                                color: swatchOf(newCatTone).deep,
                            }}
                        />
                        <Button
                            size="small"
                            className={classes.catAddBtn}
                            disabled={!newCatName.trim() || creatingCat}
                            startIcon={creatingCat
                                ? <CircularProgress size={14} thickness={5} style={{ color: '#fff' }} />
                                : <AddIcon style={{ fontSize: 16 }} />}
                            onClick={createCategory}
                        >
                            {creatingCat ? 'Adding…' : 'Add'}
                        </Button>
                    </div>

                    <div className={classes.catListLabel}>
                        Existing
                        {catAll.length > 0 && <span className={classes.catCountPill}>{catAll.length}</span>}
                        <span className={classes.sectionRuleThin} />
                        {catHiddenBelow > 0 && (
                            <span className={classes.scrollHintTop}>
                                <KeyboardArrowDownIcon style={{ fontSize: 14 }} />
                                scroll for {catHiddenBelow} more
                            </span>
                        )}
                    </div>

                    {catAllLoading ? (
                        [...Array(4)].map((_, i) => <Skeleton key={i} height={38} />)
                    ) : (
                        <div className={classes.catListWrap}>
                        <div className={classes.catList} ref={catListRef} onScroll={measureCatList}>
                            {catAll.map((c) => {
                                const sw = swatchOf(c.tone)
                                const busy = catBusyId === c._id

                                if (editCatId === c._id) {
                                    return (
                                        <div key={c._id} className={`${classes.catRow} ${classes.catRowEdit}`}>
                                            <span style={{ minWidth: 0, flex: 1 }}>
                                                <div className={classes.editFields}>
                                                    <TextField
                                                        size="small" variant="outlined" label="Name" autoFocus
                                                        value={editDraft.name}
                                                        onChange={(e) => setEditDraft((d) => ({ ...d, name: e.target.value }))}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' && editDraft.name.trim() && !busy) saveEditCat()
                                                            if (e.key === 'Escape') setEditCatId('')
                                                        }}
                                                        inputProps={{ maxLength: 60 }}
                                                        style={{ flex: '1 1 150px' }}
                                                    />
                                                    <TextField
                                                        size="small" variant="outlined" label="Description"
                                                        value={editDraft.description}
                                                        onChange={(e) => setEditDraft((d) => ({ ...d, description: e.target.value }))}
                                                        inputProps={{ maxLength: 160 }}
                                                        style={{ flex: '1 1 190px' }}
                                                    />
                                                </div>
                                                <div className={classes.editTones}>
                                                    {TONE_KEYS.map((t) => (
                                                        <Tooltip key={t} title={t}>
                                                            <span
                                                                role="button"
                                                                tabIndex={0}
                                                                aria-label={t}
                                                                aria-pressed={editDraft.tone === t}
                                                                className={`${classes.toneDotSm} ${editDraft.tone === t ? classes.toneDotOn : ''}`}
                                                                style={{ background: swatchOf(t).color }}
                                                                onClick={() => setEditDraft((d) => ({ ...d, tone: t }))}
                                                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditDraft((d) => ({ ...d, tone: t })) } }}
                                                            />
                                                        </Tooltip>
                                                    ))}
                                                    {/* Says plainly that past sends change too, rather
                                                        than letting someone discover it afterwards. */}
                                                    {c.used > 0 && (
                                                        <span className={classes.editWarn}>
                                                            {c.used} past notification{c.used === 1 ? '' : 's'} will show the new name
                                                        </span>
                                                    )}
                                                    <Button
                                                        size="small" className={classes.saveCatBtn}
                                                        disabled={!editDraft.name.trim() || busy}
                                                        startIcon={busy ? <CircularProgress size={13} thickness={5} style={{ color: '#fff' }} /> : <CheckIcon style={{ fontSize: 15 }} />}
                                                        onClick={saveEditCat}
                                                    >
                                                        {busy ? 'Saving…' : 'Save'}
                                                    </Button>
                                                    <Button size="small" className={classes.cancelCatBtn} disabled={busy} onClick={() => setEditCatId('')}>
                                                        Cancel
                                                    </Button>
                                                </div>
                                            </span>
                                        </div>
                                    )
                                }

                                return (
                                    <div key={c._id} className={`${classes.catRow} ${c.isActive ? '' : classes.catRowOff}`}>
                                        <span className={classes.catDot} style={{ background: c.isActive ? sw.color : '#c3bdb8' }} />
                                        <span style={{ minWidth: 0, flex: 1 }}>
                                            <div className={classes.catName}>
                                                {c.name}
                                                {!c.isActive && <span className={classes.catOffTag}>retired</span>}
                                            </div>
                                            {c.description && <div className={classes.catDesc}>{c.description}</div>}
                                        </span>
                                        {/* Used-on count, because retiring one that 40
                                            announcements point at is a different
                                            decision from retiring an unused one. */}
                                        <Tooltip title={`Used on ${c.used || 0} notification${c.used === 1 ? '' : 's'}`}>
                                            <span className={classes.catUsed}>{c.used || 0}</span>
                                        </Tooltip>
                                        <Tooltip title="Rename or recolour">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    disabled={busy || Boolean(editCatId)}
                                                    onClick={() => startEditCat(c)}
                                                    aria-label={`edit ${c.name}`}
                                                >
                                                    <EditIcon style={{ fontSize: 16, color: ORANGE_DARK }} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Button
                                            size="small"
                                            disabled={busy || Boolean(editCatId)}
                                            className={c.isActive ? classes.retireBtn : classes.restoreBtn}
                                            startIcon={busy ? <CircularProgress size={13} thickness={5} /> : null}
                                            onClick={() => setCategoryActive(c, !c.isActive)}
                                        >
                                            {busy ? '…' : (c.isActive ? 'Retire' : 'Restore')}
                                        </Button>
                                    </div>
                                )
                            })}
                        </div>
                        {/* Fades the last visible row rather than cutting it
                            off square, which is what made the list look
                            finished. Click to jump to the end. */}
                        {catHiddenBelow > 0 && (
                            <div
                                role="button"
                                tabIndex={0}
                                aria-label={`Show ${catHiddenBelow} more categories`}
                                className={classes.catFade}
                                onClick={() => catListRef.current && catListRef.current.scrollBy({ top: 200, behavior: 'smooth' })}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); catListRef.current && catListRef.current.scrollBy({ top: 200, behavior: 'smooth' }) } }}
                            >
                                <span className={classes.catFadePill}>
                                    <KeyboardArrowDownIcon style={{ fontSize: 15 }} />
                                    {catHiddenBelow} more
                                </span>
                            </div>
                        )}
                        </div>
                    )}

                    <div className={classes.catFootNote}>
                        Retiring hides a category from the picker — nothing is deleted, and it can be
                        restored. Renaming or recolouring one <b>also updates the notifications already
                        sent with it</b>, so a corrected label reads correctly everywhere.
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                fullWidth maxWidth="xs"
                classes={{ paper: classes.dlgPaper }}
                TransitionComponent={Grow}
                transitionDuration={250}
            >
                <div className={classes.dlgHead}>
                    <SendIcon />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div className={classes.dlgTitle}>Send to {preview.recipientCount} people?</div>
                        <div className={classes.dlgSub}>{preview.summary || 'Everyone'}</div>
                    </div>
                    <Tooltip title="Close">
                        <IconButton size="small" style={{ color: '#fff' }} onClick={() => setConfirmOpen(false)}>
                            <ClearIcon />
                        </IconButton>
                    </Tooltip>
                </div>
                <DialogContent style={{ padding: '18px 22px 6px' }}>
                    <div className={classes.confirmLine}><b>{title}</b></div>
                    <div className={classes.confirmBox}>
                        <ErrorOutlineIcon style={{ color: '#b26a00' }} />
                        <span>
                            This appears on the bell for all {preview.recipientCount} of them straight away.
                            It cannot be recalled or edited once sent.
                        </span>
                    </div>
                </DialogContent>
                <DialogActions style={{ padding: '14px 22px 18px' }}>
                    <Button className={classes.ghostBtn} onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button className={classes.sendBtn} startIcon={<SendIcon />} onClick={send}>
                        Send now
                    </Button>
                </DialogActions>
            </Dialog>

            {/* The same full-page sheet the bell opens - one component, so the
                two views cannot drift. /sent carries recipientCount, readCount
                and emailStatus, which /mine does not, so this one also shows a
                Delivery section; no flag needed, the sheet keys off the field.

                "Open page" shows here too now that it opens a new tab - it no
                longer navigates away from a half-written message. */}
            <NotificationDetailSheet
                notification={detailNotif}
                onClose={() => setDetailNotif(null)}
            />
        </div>
    )
}

export default connect(null, { setSnackInfo })(SendNotification)
